import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Chapter, Reveal } from "./Reveal";
import { PROJECTS } from "../../data/portfolio";

const spans = ["lg:col-span-12 lg:row-span-2"];

export const FeaturedWork = () => {
  const [active, setActive] = useState(null);

  return (
    <section
      id="work"
      className="relative bg-[#EEEAE2] border-y border-[#1F2D3D]/10 py-24 md:py-36"
      data-testid="work-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Chapter num="02" label="Case Studies" />

        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl font-semibold tracking-tight mb-16 max-w-3xl">
            Selected work where strategy met{" "}
            <span className="text-[#5E7E9B]">execution.</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 auto-rows-[300px] gap-5">
          {PROJECTS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08} className={spans[i % spans.length]}>
              <button
                onClick={() => setActive(p)}
                className="group relative h-full w-full text-left rounded-xl overflow-hidden border border-[#1F2D3D]/10 hover:border-[#5E7E9B]/60 transition-colors duration-500"
                data-testid={`project-card-${p.id}`}
              >
                <div className="absolute inset-0 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-[opacity,transform] duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1F2D3D] via-[#1F2D3D]/45 to-transparent" />
                </div>
                <div className="relative h-full flex flex-col justify-between p-7">
                  <div className="flex items-center justify-between">
                    <span
                      className="font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full border"
                      style={{ color: p.accent, borderColor: `${p.accent}55` }}
                    >
                      {p.category}
                    </span>
                    <span className="font-mono text-xs text-[#7A8492]">{p.year}</span>
                  </div>
                  <div>
                    <h3 className="font-display text-2xl md:text-3xl font-medium leading-tight max-w-md text-white/90 group-hover:text-white transition-colors">
                      {p.title}
                    </h3>
                    <div className="mt-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#E9E6DF] group-hover:text-[#5E7E9B] transition-colors">
                      View case study
                      <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <CaseStudyModal project={active} onClose={() => setActive(null)} />
        )}
      </AnimatePresence>
    </section>
  );
};

const CaseStudyModal = ({ project, onClose }) => {
  useEffect(() => {
    document.documentElement.classList.add("lenis-stopped");
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.documentElement.classList.remove("lenis-stopped");
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[70] flex items-start md:items-center justify-center p-0 md:p-6"
    data-testid="case-study-modal"
  >
    <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
    <motion.div
      initial={{ y: 60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 40, opacity: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      data-lenis-prevent
      className="relative w-full max-w-4xl max-h-full md:max-h-[90vh] overflow-y-auto overscroll-contain bg-white border border-[#1F2D3D]/10 md:rounded-2xl"
      style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
    >
      <button
        onClick={onClose}
        className="fixed md:absolute top-4 right-4 z-10 w-11 h-11 grid place-items-center rounded-full glass border border-[#1F2D3D]/10 text-[#1F2D3D] hover:bg-[#5E7E9B] hover:text-white transition-colors"
        data-testid="modal-close"
      >
        <X size={18} />
      </button>

      <div className="relative h-64 md:h-80 overflow-hidden">
        <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-white to-transparent" />
        <div className="absolute bottom-0 left-0 p-8">
          <span
            className="font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full border"
            style={{ color: project.accent, borderColor: `${project.accent}55` }}
          >
            {project.category} · {project.year}
          </span>
          <h3 className="mt-4 font-display text-3xl md:text-5xl font-semibold tracking-tight">
            {project.title}
          </h3>
        </div>
      </div>

      <div className="p-8 space-y-10">
        <p className="text-xl text-[#404B5A] leading-relaxed">{project.overview}</p>

        <div className="grid md:grid-cols-2 gap-8">
          <Field label="My Role">{project.role}</Field>
          <Field label="Skills Used">
            <div className="flex flex-wrap gap-2 mt-1">
              {project.skills.map((s) => (
                <span key={s} className="font-mono text-[11px] px-3 py-1 rounded-full bg-[#1F2D3D]/[0.04] border border-[#1F2D3D]/10 text-[#404B5A]">
                  {s}
                </span>
              ))}
            </div>
          </Field>
        </div>

        <Field label="The Process">{project.process}</Field>

        {project.gallery && <DeckGallery gallery={project.gallery} accent={project.accent} />}

        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9AA3AF] mb-4">
            Results & Impact
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {project.results.map((r, i) => (
              <div key={i} className="p-5 rounded-lg bg-[#1F2D3D]/[0.04] border border-[#1F2D3D]/10" data-testid={`result-${i}`}>
                <span className="font-display text-lg" style={{ color: project.accent }}>
                  {r.split(" ")[0]}
                </span>
                <p className="mt-1 text-sm text-[#5B6472]">{r.split(" ").slice(1).join(" ")}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  </motion.div>
  );
};

const DeckGallery = ({ gallery, accent }) => {
  const [idx, setIdx] = useState(0);
  const prev = () => setIdx((i) => (i - 1 + gallery.length) % gallery.length);
  const next = () => setIdx((i) => (i + 1) % gallery.length);

  return (
    <div data-testid="deck-gallery">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9AA3AF] mb-4">
        From the Deck
      </div>
      <div className="relative rounded-xl overflow-hidden border border-[#1F2D3D]/10 bg-[#1F2D3D]/[0.03]">
        <AnimatePresence mode="wait">
          <motion.img
            key={idx}
            src={gallery[idx].src}
            alt={gallery[idx].caption}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full aspect-[16/9] object-contain"
            data-testid="gallery-image"
          />
        </AnimatePresence>
        <button
          onClick={prev}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center rounded-full bg-white/90 shadow-md border border-[#1F2D3D]/10 text-[#1F2D3D] hover:bg-[#5E7E9B] hover:text-white transition-colors"
          data-testid="gallery-prev"
          aria-label="Previous slide"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          onClick={next}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center rounded-full bg-white/90 shadow-md border border-[#1F2D3D]/10 text-[#1F2D3D] hover:bg-[#5E7E9B] hover:text-white transition-colors"
          data-testid="gallery-next"
          aria-label="Next slide"
        >
          <ChevronRight size={18} />
        </button>
      </div>
      <div className="mt-3 flex items-start justify-between gap-4">
        <p className="text-sm text-[#5B6472] leading-relaxed" data-testid="gallery-caption">
          {gallery[idx].caption}
        </p>
        <span className="font-mono text-xs shrink-0" style={{ color: accent }} data-testid="gallery-counter">
          {idx + 1} / {gallery.length}
        </span>
      </div>
      <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-lenis-prevent>
        {gallery.map((g, i) => (
          <button
            key={g.src}
            onClick={() => setIdx(i)}
            className={`shrink-0 w-16 h-10 rounded-md overflow-hidden border transition-colors ${
              i === idx ? "border-[#5E7E9B]" : "border-[#1F2D3D]/10 opacity-60 hover:opacity-100"
            }`}
            data-testid={`gallery-thumb-${i}`}
            aria-label={`Slide ${i + 1}`}
          >
            <img src={g.src} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9AA3AF] mb-2">
      {label}
    </div>
    <div className="text-[#404B5A] leading-relaxed">{children}</div>
  </div>
);
;
